<template>
    <div>
        <ValidationObserver ref="scholarshipFormValidate">
            <b-row>
                <b-col cols="12" md="10">
                    <!-- BISEP (BS) Role UI -->
                    <template v-if="activeRoleCode === 'BISEP'">
                        <b-row>
                            <b-col cols="12" md="6">
                                <b-form-group :label="$t('status')">
                                    <ValidationProvider name="status" rules="required" v-slot="{ errors }">
                                        <multi-selectbox v-model="scholarshipFormData.status"
                                                         :options="statusOptions"
                                                         :multiple="false"
                                                         :validateError="errors[0]"/>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <b-row v-if="scholarshipFormData.status === 'approved'">
                            <b-col cols="12" md="6">
                                <b-form-group :label="$t('scholarship_rate')">
                                    <ValidationProvider name="scholarship_rate" rules="required" v-slot="{ errors }">
                                        <multi-selectbox v-model="scholarshipFormData.scholarship_rate"
                                                         :options="scholarshipOptions"
                                                         :multiple="false"
                                                         :validateError="errors[0]"/>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="12">
                                <b-form-group :label="$t('description')">
                                    <ValidationProvider name="scholarship_description" rules="required" v-slot="{ errors }">
                                        <b-form-textarea v-model="scholarshipFormData.scholarship_description" rows="3"
                                                         :state="errors[0] ? false : null" />
                                        <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <b-row v-if="scholarshipFormData.status === 'declined'">
                            <b-col cols="12">
                                <b-form-group :label="$t('decline_reason')">
                                    <ValidationProvider name="decline_reason" rules="required" v-slot="{ errors }">
                                        <b-form-textarea v-model="scholarshipFormData.decline_reason" rows="3"
                                                         :state="errors[0] ? false : null" />
                                        <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </template>

                    <template v-else-if="activeRoleCode === 'CH'">
                        <div class="bg-light p-3 border rounded mb-4">
                            <p><strong>{{ $t('scholarship_rate') }}:</strong> {{ formData.scholarship_rate || '-' }}</p>
                            <p><strong>{{ $t('description') }}:</strong> {{ formData.scholarship_description || '-' }}</p>
                        </div>

                        <b-row>
                            <b-col cols="12" md="6">
                                <b-form-group :label="$t('status')">
                                    <ValidationProvider name="status" rules="required" v-slot="{ errors }">
                                        <multi-selectbox v-model="scholarshipFormData.status"
                                                         :options="statusOptions"
                                                         :multiple="false"
                                                         :validateError="errors[0]"/>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <b-row v-if="scholarshipFormData.status === 'declined'">
                            <b-col cols="12">
                                <b-form-group :label="$t('decline_reason')">
                                    <ValidationProvider name="decline_reason" rules="required|max:1000" v-slot="{ errors }">
                                        <b-form-textarea v-model="scholarshipFormData.decline_reason" rows="3"
                                                         :state="errors[0] ? false : null" />
                                        <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </template>

                    <template v-else>
                        <b-alert show variant="warning">{{ $t('unauthorized_operation') }}</b-alert>
                    </template>
                </b-col>
            </b-row>

            {{activeRoleCode}}
            <div class="d-flex justify-content-center mb-3 mt-4" v-if="['BISEP', 'CH'].includes(activeRoleCode)">
                <processing-button :processing="loading" variant="primary"
                                   :label="$t('save')" @click="submitScholarship" />
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import MultiSelectbox from "@/components/interactive-fields/MultiSelectbox"
import ProcessingButton from '@/components/elements/ProcessingButton'
import BisepService from "@/services/BisepService"

export default {
    name: 'ScholarshipTab',
    components: {
        ValidationObserver,
        ValidationProvider,
        MultiSelectbox,
        ProcessingButton
    },
    props: {
        formId: {
            type: Number,
            required: true
        },
        formData: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            scholarshipFormData: {
                status: null,
                scholarship_rate: null,
                scholarship_description: null,
                decline_reason: null
            },
            statusOptions: [
                { text: this.$t('approve'), value: 'approved' },
                { text: this.$t('reject'), value: 'declined' }
            ],
            scholarshipOptions: [
                { value: 10, text: '%10' },
                { value: 20, text: '%20' },
                { value: 30, text: '%30' },
                { value: 40, text: '%40' },
                { value: 50, text: '%50' },
                { value: 100, text: '%100' }
            ]
        }
    },
    computed: {
        activeRoleCode() {
            return  this.$store.getters['auth/getActiveRole'];
        }
    },
    methods: {
        async submitScholarship() {
            const isValid = await this.$refs.scholarshipFormValidate.validate()
            if (isValid) {
                this.loading = true;
                const payload = {
                    bisep_application_id: this.formId,
                    status: this.scholarshipFormData.status,
                    ...this.scholarshipFormData
                };
                if (this.scholarshipFormData.status === 'approved') {
                    delete payload.decline_reason;
                } else {
                    delete payload.scholarship_rate;
                    delete payload.scholarship_description;
                }

                const apiCall = this.activeRoleCode === 'BISEP'
                    ? BisepService.approveApplicationForBisep
                    : BisepService.approveApplicationForFinance;

                apiCall(payload).then(response => {
                    this.$toast.success(this.$t('api.' + response.data.message));
                    this.loading = false;
                    this.$emit('updated', true);
                }).catch(e => {
                    this.loading = false;
                    this.showErrors(e, this.$refs.scholarshipFormValidate);
                });
            }
        }
    }
}
</script>
